import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";

type DepartmentItem = { id: string; name: string };
type PositionItem = { id: string; title: string };

const PositionFormSchema = z.object({
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຕໍາແໜ່ງ"),
  departmentId: z.string().min(1, "ກະລຸນາເລືອກພະແນກ"),
  parentId: z.string().optional().nullable(),
  maxEmployees: z
    .any()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = typeof val === "string" ? Number(val) : val;
      if (typeof num === "number" && !Number.isNaN(num) && num > 0) {
        return Math.floor(num);
      }
      return null;
    })
    .refine(
      (val): val is number | null =>
        val === null || (typeof val === "number" && val > 0),
      {
        message: "ຈໍານວນພະນັກງານຕ້ອງເປັນຈໍານວນບວກ",
      },
    )
    .nullable()
    .optional(),
});
export type PositionFormValues = z.infer<typeof PositionFormSchema>;

export function PositionForm({
  initialValues,
  onSubmit,
  submitting,
  excludePositionId,
}: {
  initialValues?: Partial<PositionFormValues>;
  onSubmit: (values: PositionFormValues) => void;
  submitting?: boolean;
  excludePositionId?: string; // Exclude this position from parent dropdown
}) {
  const methods = RHF.useForm<PositionFormValues>({
    resolver: zodResolver(PositionFormSchema),
    defaultValues: {
      title: "",
      departmentId: "",
      parentId: null,
      maxEmployees: null,
      ...initialValues,
    },
  });

  return (
    <FormRoot<PositionFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <FormInput
        name="title"
        label="ຊື່ຕໍາແໜ່ງ"
        requiredMark
        placeholder="ຕົວຢ່າງ: ຜູ້ຈັດການ, ພະນັກງານ"
      />
      <FormInfiniteCombobox<DepartmentItem>
        name="departmentId"
        label="ພະແນກ"
        requiredMark
        queryKey={["departments"]}
        queryFn={(args) =>
          fetchLookupForInfinite(
            `${config.apiUrl}/positions/departments/lookup`,
            args,
          )
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/positions/departments/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກພະແນກ..."
      />
      <FormInfiniteCombobox<PositionItem>
        name="parentId"
        label="ຕໍາແໜ່ງທີ່ລາຍງານໄປຫາ (ຫົວໜ້າ)"
        queryKey={["positions", excludePositionId ?? ""]}
        queryFn={async (args) => {
          const baseUrl = `${config.apiUrl}/positions/lookup`;
          const url = excludePositionId
            ? `${baseUrl}?exclude=${excludePositionId}`
            : baseUrl;
          const result = await fetchLookupForInfinite<{
            id: string;
            name: string;
          }>(url, args);
          return {
            items: result.items.map((item) => ({
              id: item.id,
              title: item.name,
            })),
            nextPage: result.nextPage,
          };
        }}
        preloadQueryFn={async (id) => {
          const item = await hydrateLookupItem<{ id: string; name: string }>(
            `${config.apiUrl}/positions/lookup`,
            id,
          );
          if (!item) return null;
          return { id: item.id, title: item.name };
        }}
        getLabel={(item) => item.title}
        getValue={(item) => item.id}
        placeholder="ເລືອກຕໍາແໜ່ງຫົວໜ້າ (ບໍ່ບັງຄັບ)"
        clearable
      />
      <FormNumber
        name="maxEmployees"
        label="ຈໍານວນພະນັກງານສູງສຸດ"
        placeholder="ບໍ່ຈໍາກັດ (ປ່ອຍເປົ່າ)"
      />

      <div className="flex justify-end gap-2">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
